import { Box } from '@mui/material'

import {
  BingChannel,
  Facebook,
  GoogleAds,
  LinkedinAds,
  TiktokAds,
} from 'assets/icons'
import FeatherIcon from 'feather-icons-react'

export const CHANNEL_ICON_BOX_PX = 20
export const CHANNEL_ICON_PX = 16

const iconStyle = (boxPx: number, iconPx: number) =>
  ({
    width: iconPx,
    height: iconPx,
    maxWidth: boxPx,
    maxHeight: boxPx,
    display: 'block',
    flexShrink: 0,
  }) as const

type ChannelIconProps = {
  /** API / stats field: e.g. `facebook`, `folder`, `arrow-right` (Feather name). */
  channelIcon: string
  /** Pixel size for the glyph (default matches reports channels table). */
  size?: number
}

/**
 * Renders the same channel glyph as Reports → Traffic → Channels (brand SVGs or Feather fallback).
 */
export const ChannelIcon = ({
  channelIcon,
  size = CHANNEL_ICON_PX,
}: ChannelIconProps) => {
  const boxPx = Math.max(size, CHANNEL_ICON_BOX_PX)
  const iconSx = iconStyle(boxPx, size)
  if (channelIcon === 'facebook' || channelIcon === 'meta') {
    return <Facebook style={iconSx} />
  }
  if (channelIcon === 'tiktok') {
    return <TiktokAds style={iconSx} />
  }
  if (channelIcon === 'linkedin') {
    return <LinkedinAds style={iconSx} />
  }
  if (channelIcon === 'google') {
    return <GoogleAds style={iconSx} />
  }
  if (channelIcon === 'bing') {
    return <BingChannel style={iconSx} />
  }
  return (
    <FeatherIcon
      icon={channelIcon}
      size={size}
    />
  )
}

type ChannelIconBoxProps = {
  channelIcon: string
  size?: number
  /** Outer box side length; default `max(size, 20)` for table rows. Use with `size` for compact chips. */
  boxSize?: number
}

/** Fixed box so brand SVGs cannot stretch to full row height (matches reports table). */
export const ChannelIconBox = ({
  channelIcon,
  size = CHANNEL_ICON_PX,
  boxSize,
}: ChannelIconBoxProps) => {
  const box = boxSize ?? Math.max(size, CHANNEL_ICON_BOX_PX)
  return (
    <Box
      sx={{
        width: box,
        height: box,
        minWidth: box,
        minHeight: box,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
        color: 'text.primary',
        '& svg': {
          maxWidth: box,
          maxHeight: box,
          display: 'block',
        },
      }}>
      <ChannelIcon
        channelIcon={channelIcon}
        size={size}
      />
    </Box>
  )
}
